/**
 * Spanish locale for extensions-ui.
 * Export is the "extensions" subtree: host apps merge this into their i18n
 * resources as translation.extensions so that t('extensions.list.heading') etc. resolve.
 */
import type { ExtensionsLocale } from './en'

export const extensionsEs = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `Cancelar`,
    delete: `Eliminar`,
    save: `Guardar`,
    apply: `Aplicar`,
    edit: `Editar`,
    actions: `Acciones`,
    add: `Agregar`,
    all: `Todos`,
    loading: `Cargando...`,
    noChange: `Sin cambios`,
    none: `Ninguno`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    pageLabel: `Todas las extensiones`,
    heading: `Extensiones`,
    linesHeading: `Líneas`,
    searchPlaceholder: `Buscar por nombre o número`,
    linesSearchPlaceholder: `Buscar por nombre, número, perfil asignado o ubicación`,
    loadingExtensions: `Cargando extensiones...`,
    noMatch: `Ninguna extensión coincide con su búsqueda`,
    noExtensions: `No se encontraron extensiones`,
    noMatchHint: `Pruebe con un término de búsqueda diferente.`,
    noExtensionsHint: `No hay extensiones para este PBX.`,
    activateAll: `Activar todo`,
    deactivateAll: `Desactivar todo`,
    massEdit: `Edición masiva`,
    linesSelected: `{{count}} línea{{suffix}} seleccionada{{suffix}}`,
  },

  // ---------------------------------------------------------------------------
  // Column headers
  // ---------------------------------------------------------------------------
  columns: {
    number: `Número`,
    name: `Nombre`,
    type: `Tipo`,
    created: `Creado`,
    updated: `Actualizado`,
    lastUpdated: `Última actualización`,
    assignedUser: `Usuario asignado`,
    assignedProfile: `Perfil asignado`,
    location: `Ubicación`,
    actions: `Acciones`,
  },

  // ---------------------------------------------------------------------------
  // Row actions & badges
  // ---------------------------------------------------------------------------
  row: {
    inactive: `Inactivo`,
    edit: `Editar`,
    delete: `Eliminar`,
    viewCallMetrics: `Ver métricas de llamadas`,
    activate: `Activar`,
    deactivate: `Desactivar`,
    extensionFallback: `Extensión {{id}}`,
    selectAllLines: `Seleccionar todas las líneas`,
    selectLine: `Seleccionar {{name}}`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `Eliminar extensión`,
    confirmation: `¿Está seguro de que desea eliminar "{{displayName}}"? Esta acción no se puede deshacer.`,
    unnamed: `Extensión sin nombre`,
    reassignLabel: `Reasignar referencias a`,
    deleteWithoutReassigning: `Eliminar sin reasignar`,
  },

  // ---------------------------------------------------------------------------
  // Set active state dialog (single)
  // ---------------------------------------------------------------------------
  activeStateDialog: {
    activateTitle: `Activar extensión`,
    deactivateTitle: `Desactivar extensión`,
    body: `Está a punto de {{action}} {{displayName}}, lo que provocará que todos los dispositivos que usan esta extensión como la Extensión de Línea Principal se reinicien.`,
    unnamedLine: `Línea sin nombre`,
    confirmPrompt: `¿Está seguro de que desea continuar?`,
    setActive: `Establecer activo`,
    setInactive: `Establecer inactivo`,
  },

  // ---------------------------------------------------------------------------
  // Bulk active state dialog
  // ---------------------------------------------------------------------------
  bulkActiveDialog: {
    activateTitle: `Activar todas las extensiones`,
    deactivateTitle: `Desactivar todas las extensiones`,
    body: `Está a punto de {{action}} todas las extensiones, lo que provocará que todos los dispositivos que usan estas extensiones como la Extensión de Línea Principal se reinicien.`,
    confirmPrompt: `¿Está seguro de que desea continuar?`,
    activateAll: `Activar todo`,
    deactivateAll: `Desactivar todo`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `Edición masiva de líneas`,
    description: `Actualice los siguientes campos para {{count}} línea{{suffix}}. Deje como "Sin cambios" para mantener el valor actual.`,
    outboundCallerId: `ID de llamada saliente`,
    localPresence: `Presencia local`,
    preferredCodec: `Códec preferido`,
    location: `Ubicación`,
    locationPlaceholder: `Sin cambios o escriba para buscar`,
    locationSearchLabel: `Búsqueda de ubicación`,
    loadingLocations: `Cargando ubicaciones\u2026`,
    noLocationsFound: `No se encontraron ubicaciones`,
    typeToSearchLocations: `Escriba para buscar ubicaciones`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `Resultados por página:`,
    previousPage: `Página anterior`,
    nextPage: `Página siguiente`,
    all: `Todos`,
    jumpToPage: `Ir a la página {{page}}`,
    searchExtensions: `Buscar extensiones`,
    toggleFilters: `Alternar filtros`,
    showingAll: `Mostrando los {{total}} resultados`,
    showingRange: `Mostrando {{start}} a {{end}} de {{total}} resultados`,
  },

  // ---------------------------------------------------------------------------
  // Generic edit pages
  // ---------------------------------------------------------------------------
  edit: {
    validationError: `Error de validación`,
    reviewRequiredFields: `Revise los campos requeridos e inténtelo de nuevo.`,
    emailFormatInvalid: `Formato de correo electrónico incorrecto. Asegúrese de que no haya espacios. Separe varias direcciones con comas.`,
    nameNumberRequired: `El nombre y el número son obligatorios.`,
    reservedNumber: `{{number}} está reservado. Seleccione un número diferente.`,
    createFailed: `Error al crear`,
    updateFailed: `Error al actualizar`,
    breadcrumbAdd: `Agregar {{typeLabel}}`,
    breadcrumbEdit: `Editar {{typeLabel}} {{value}}`,
    backToList: `← Volver a {{listLabel}}`,
    wrongPbx: `Esta extensión no está en la PBX seleccionada`,
    wrongPbxDescription: `Esta extensión no pertenece a la PBX seleccionada.`,
    openInPbx: `Abrir en {{pbxName}}`,
    loadFailed: `No se pudo cargar esta extensión`,
    options: `Opciones`,
    saveChanges: `Guardar cambios`,
    omitFromDirectory: `Omitir del directorio`,
    nameRequired: `El nombre es obligatorio`,
    numberRequired: `El número es obligatorio`,
    extensionNumberPlaceholder: `Número de extensión`,
    yes: `Sí`,
    no: `No`,
    members: `Miembros`,
    membersNoun: `miembros`,
    userGroups: `Grupos de usuarios`,
    userGroupsNoun: `grupos de usuarios`,
    holdMusic: `Música en espera`,
    line: {
      requiredFields: `El nombre, número, ubicación y perfil asignado son obligatorios.`,
      selectLocation: `Seleccione una ubicación`,
      notType: `No es una línea`,
      pbxUuid: `UUID del PBX`,
      pbxName: `PBX`,
      sipSecret: `Secreto SIP`,
      sipSecretReset: `Se restableció el secreto SIP`,
      reset: `Restablecer`,
      extensionUuid: `UUID de la Extensión`,
      dialNow: `Marcar ahora`,
      dialNowCleared: `Dial-now borrado`,
      clear: `Borrar`,
      assignedProfileMobileDevices: `Dispositivos móviles del perfil asignado`,
      addTitle: `Agregar línea`,
      title: `Línea`,
      voicemailOptions: `Buzón de Voz`,
      outDialExtension: `Extensión OutDial`,
      outDialHelp: `Ayuda de extensión OutDial`,
      outDialHelpNo: `No: Esta extensión actúa como una extensión regular.`,
      outDialHelpYes: `Sí: Esta extensión solo reenvía al Número de desvío.`,
      outDialHelpBeAware: `Tenga en cuenta:`,
      outDialHelpWarningOne: `El sistema no puede recuperar una llamada una vez que se ha reenviado a un número externo.`,
      outDialHelpWarningTwo: `No puede usar el buzón de voz del sistema con una extensión OutDial.`,
      outDialEnabledHint: `OutDial está habilitado. Las llamadas se reenvían al Número de desvío; el ID de llamada saliente sigue aplicándose en la pierna saliente.`,
      outDialDisabledHint: `OutDial está deshabilitado. Se aplican las opciones de llamada saliente.`,
      loadingProfiles: `Cargando perfiles…`,
      voicemailEnabled: `Buzón de voz habilitado`,
      forwardingNumber: `Número de desvío (OutDial)`,
      localAreaCode: `Código de área local`,
      localAreaCodeInvalid: `El código de área no es válido. {{areaCode}} no está permitido para el código de área local.`,
      localAreaCodeNotAllowed: `El código de área {{areaCode}} no está permitido para el código de área local.`,
      outboundCallerIdRequired: `Seleccione un ID de llamada saliente.`,
      outboundCallerIdNoOptions: `Este PBX no tiene números de teléfono configurados. Añada un número al PBX antes de editar esta línea.`,
      outboundCallerIdMissingSavedValue: `Esta línea no tiene un ID de llamada saliente válido. Seleccione uno y guarde antes de continuar.`,
      expandDetails: `Mostrar detalles`,
      collapseDetails: `Ocultar detalles`,
      activate: `Activar`,
      deactivate: `Desactivar`,
      findMe: {
        title: `Encuéntrame`,
        mode: `Encuéntrame`,
        number: `Número de Encuéntrame`,
        delay: `Encuéntrame después de`,
        modeOptions: {
          '0': `Desactivado`,
          '1': `Activado`,
          '2': `Sonar todos`,
        },
        delayOptions: {
          '0': `Inmediatamente`,
          '5': `5 segundos`,
          '10': `10 segundos`,
          '15': `15 segundos`,
        },
        numberRequired: `Se debe proporcionar un número de Encuéntrame si Encuéntrame está activado.`,
        numberInvalid: `El número de Encuéntrame solo puede contener dígitos y un + inicial opcional (máximo 15 dígitos).`,
      },
      callRecording: `Grabación de llamadas`,
      callRecordingOptions: {
        '0': `No`,
        '1': `Sí`,
        '2': `Definido por el sistema`,
      },
      dedicatedFax: `Fax dedicado`,
      inCallFeatureCodes: `Códigos de función en llamada habilitados`,
      createButton: `Crear línea`,
      saveAndCreateAnother: `Guardar y crear otra`,
      details: `Detalles de línea`,
      codecOpus: `Mejor para internet lento (OPUS) Compatible con: Web Phones, Yealink`,
      codecPcmu: `Estándar (PCMU) Compatible con: Todos los tipos de teléfono`,
      codecG722: `Audio HD (G722) Compatible con: Yealink, Cisco`,
      codecG729: `OBSOLETO - Bueno para internet lento (G729) Compatible con: Yealink, Cisco`,
    },
    ringGroup: {
      ringing: `Timbre`,
      created: `Grupo de timbrado creado`,
      updated: `Grupo de timbrado actualizado`,
      saveFailed: `No se pudo guardar el grupo de timbrado`,
      notType: `No es un grupo de timbrado`,
      notTypeDescription: `Esta extensión no es un grupo de timbrado. Use la lista de extensiones para editar otros tipos.`,
      namePlaceholder: `Nombre del grupo de timbrado`,
      ringStrategy: `Estrategia de timbrado`,
      ringAll: `Timbre a todos`,
      pagingGroup: `Grupo de búsqueda`,
      ringbackAudio: `Audio de retorno de timbrado`,
      createButton: `Crear grupo de timbrado`,
      updateButton: `Actualizar grupo de timbrado`,
    },
    callQueue: {
      created: `Cola de llamadas creada`,
      updated: `Cola de llamadas actualizada`,
      saveFailed: `No se pudo guardar la cola de llamadas`,
      notType: `No es una cola de llamadas`,
      queueSettings: `Configuración de cola`,
      ringStrategy: `Estrategia de timbrado`,
      strategyRingAll: `Timbre a todos`,
      strategyRoundRobin: `Round Robin`,
      strategyLongestIdle: `Más inactivo`,
      strategyRandom: `Aleatorio`,
      strategyLeastTalkTime: `Menor tiempo de conversación`,
      strategyFewestCalls: `Menos llamadas`,
      preCallAnnouncement: `Anuncio previo a la llamada`,
      maxNoAnswer: `Máx. sin respuesta (intentos)`,
      busyDelayTime: `Tiempo de espera por ocupado (segundos)`,
      rejectDelayTime: `Tiempo de espera por rechazo (segundos)`,
      wrapUpTime: `Tiempo de cierre (segundos)`,
      noAnswerDelayTime: `Tiempo sin respuesta (segundos)`,
      maxRingDuration: `Duración máxima de timbrado (segundos)`,
      maxCallersInQueue: `Máximo de llamadas en cola`,
      maxWaitTime: `Tiempo máximo de espera (segundos)`,
      timeoutDestination: `Destino por tiempo agotado`,
      escapeDestination: `Destino de escape`,
      destinationSearchPlaceholder: `Buscar extensiones...`,
      destinationNoMatches: `Ninguna extensión coincide con esa búsqueda.`,
      destinationCurrentUnlisted: `{{value}} (actual)`,
      periodicAnnouncements: `Anuncios periódicos`,
      positionAnnouncementFrequency: `Frecuencia de anuncio de posición`,
      positionAnnouncementMessage: `Mensaje de anuncio de posición`,
      queuePosition: `Posición en cola`,
      agentsNoun: `agentes`,
      newButton: `Nueva cola de llamadas`,
      updateButton: `Actualizar cola de llamadas`,
    },
    conferenceRoom: {
      moderatorPinValidation: `El PIN del moderador debe tener exactamente 4 dígitos.`,
      conferencePinValidation: `El PIN de conferencia debe tener exactamente 4 dígitos.`,
      holdMusicRequired: `La música en espera es obligatoria.`,
      created: `Sala de conferencias creada`,
      updated: `Sala de conferencias actualizada`,
      saveFailed: `No se pudo guardar la sala de conferencias`,
      notType: `No es una sala de conferencias`,
      moderatorRequired: `Moderador requerido`,
      moderatorPin: `PIN del moderador`,
      userPinRequired: `PIN de usuario requerido`,
      conferencePin: `PIN de conferencia`,
      defaultMuted: `Silenciado por defecto`,
      announceNewParticipants: `Anunciar nuevos participantes`,
      conferenceGreeting: `Saludo de conferencia`,
      selectHoldMusic: `Seleccionar música en espera`,
      createButton: `Crear sala de conferencias`,
      updateButton: `Actualizar sala de conferencias`,
    },
    virtualFax: {
      invalidEmail: `Por favor, ingrese una dirección de correo electrónico válida.`,
      passwordsDoNotMatch: `Las contraseñas no coinciden.`,
      passwordMinLength: `La contraseña de fax seguro debe tener al menos 8 caracteres.`,
      created: `Fax virtual creado`,
      updated: `Fax virtual actualizado`,
      saveFailed: `No se pudo guardar el fax virtual`,
      secureFaxLinkResent: `Se reenvió el enlace de fax seguro`,
      resendFailed: `Error al reenviar`,
      resendSaveFailed: `No se pudo reenviar el enlace de fax seguro`,
      notType: `No es un fax virtual`,
      receivingOptions: `Opciones de recepción`,
      receivingEmailDestination: `Correo de recepción`,
      secureFax: `Fax seguro`,
      download: `Descarga`,
      singleDownload: `Descarga única`,
      download5: `5 descargas`,
      download10: `10 descargas`,
      download15: `15 descargas`,
      failedLoginRetries: `Límite de intentos de contraseña fallidos`,
      retries5: `5 reintentos`,
      retries10: `10 reintentos`,
      retries15: `15 reintentos`,
      linkExpiresIn: `El enlace de descarga expira en`,
      day1: `1 día`,
      day2: `2 días`,
      day3: `3 días`,
      day7: `7 días`,
      day15: `15 días`,
      day30: `30 días`,
      changePasswordOptional: `Cambiar contraseña de fax seguro (opcional)`,
      userGroupsDescription: `Elija qué grupos de usuarios tendrán acceso a este fax virtual.`,
      sending: `Enviando…`,
      resendSecureFaxLink: `Reenviar enlace de fax seguro`,
      createButton: `Crear máquina de fax virtual`,
      updateButton: `Actualizar máquina de fax virtual`,
    },
    shared: {
      generalOptions: `Opciones generales`,
      voicemailTimeZone: `Zona horaria del buzón de voz`,
      voicemailPassword: `Contraseña del buzón de voz`,
      playVoicemailInstructions: `Reproducir instrucciones del buzón`,
      notificationOptions: `Opciones de notificación`,
      sendEmailNotifications: `Enviar notificaciones por correo`,
      messageEmailAddress: `Correo para mensajes`,
      emailPlaceholder: `correo@ejemplo.com`,
      attachVoicemailMessage: `Adjuntar mensaje de buzón`,
      deleteMessageAfterSend: `Eliminar mensaje después de enviar`,
      outgoingMessage: `Mensaje saliente`,
      useSystemGreetings: `Usar saludos del sistema`,
      customGreeting: `Saludo personalizado`,
    },
    sharedVoicemail: {
      created: `Buzón compartido creado`,
      updated: `Buzón compartido actualizado`,
      saveFailed: `No se pudo guardar el buzón compartido`,
      notType: `No es un buzón compartido`,
      userGroupsDescription: `Elija qué grupos de usuarios tendrán acceso a este buzón de voz compartido.`,
      createButton: `Crear buzón compartido`,
      updateButton: `Actualizar buzón compartido`,
    },
  },

  updateLocation: {
    title: `Actualizar ubicación`,
    description: `¿Actualizar ubicación para {{count}} línea{{suffix}}?`,
    confirm: `Actualizar ubicación`,
  },

  pickList: {
    assignedWithLimit: `Asignados {{itemNoun}}: {{count}} / {{max}}`,
    assigned: `Asignados {{itemNoun}}: {{count}}`,
    noneAssigned: `Aún no hay {{itemNoun}} agregados.`,
    remove: `Quitar`,
    available: `Disponibles {{itemNoun}}: {{count}}`,
    searchAvailable: `Buscar {{itemNoun}} disponibles`,
    maxReached: `Se alcanzó el máximo de {{max}} {{itemNoun}}.`,
    noneAvailable: `No hay {{itemNoun}} disponibles.`,
  },

  localPresence: {
    0: `Apagado`,
    1: `Grupo 1`,
    2: `Grupo 2`,
    3: `Grupo 3`,
    4: `Grupo 4`,
    5: `Grupo 5`,
    6: `Grupo 6`,
  },

  codecOptions: {
    opus: `Mejor para internet lento (OPUS) — Compatible con: Web Phones, Yealink`,
    pcmu: `Estándar (PCMU) — Compatible con: Todos los tipos de teléfono`,
    g722: `Audio HD (G722) — Compatible con: Yealink, Cisco`,
    g729: `OBSOLETO — Bueno para internet lento (G729) — Compatible con: Yealink, Cisco`,
    default: `Predeterminado del sistema`,
  },

  timezones: {
    america_new_york: `Hora del Este`,
    america_chicago: `Hora Central`,
    america_denver: `Hora de la Montaña`,
    america_los_angeles: `Hora del Pacífico`,
    america_phoenix: `Arizona`,
    america_anchorage: `Hora de Alaska`,
    pacific_honolulu: `Hora de Hawái`,
    america_toronto: `Toronto (Este)`,
    america_vancouver: `Hora del Pacífico - Columbia Británica (PCT)`,
    europe_london: `Londres (GMT/BST)`,
    europe_paris: `París (CET)`,
    australia_sydney: `Sídney (AEST)`,
    utc: `UTC`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deleteSuccess: `Extensión eliminada correctamente.`,
    deleteFailed: `Error al eliminar`,
    deleteFailedDescription: `No se pudo eliminar la extensión. Inténtelo de nuevo.`,
    success: `Éxito`,
    failed: `Error`,
    activeStateSuccess: `Línea establecida como {{state}}.`,
    activeStateFailed: `No se pudo actualizar el estado activo de la línea.`,
    bulkSuccess: `{{count}} línea{{suffix}} establecida{{suffix}} como {{state}}.`,
    partialFailure: `Fallo parcial`,
    partialFailureDescription: `{{succeeded}} exitosos, {{failed}} fallidos. Es posible que algunas líneas no se hayan actualizado.`,
    massEditSuccess: `{{count}} línea{{suffix}} actualizada{{suffix}}.`,
    massEditFailed: `Error en edición masiva`,
    massEditFailedDescription: `No se pudieron actualizar las líneas seleccionadas. Inténtelo de nuevo.`,
  },

  // ---------------------------------------------------------------------------
  // Extension type labels
  // ---------------------------------------------------------------------------
  types: {
    line: `Línea`,
    ringGroup: `Grupo de timbrado`,
    virtualFax: `Máquina de fax virtual`,
    conferenceRoom: `Sala de conferencias`,
    switchboard: `Cuadro de mandos`,
    sharedVoicemail: `Buzón de voz compartido`,
    callQueue: `Cola de llamadas`,
  },

  // ---------------------------------------------------------------------------
  // View mode labels
  // ---------------------------------------------------------------------------
  viewModes: {
    extensions: `Extensiones`,
    lines: `Líneas`,
    ringGroups: `Grupos de timbrado`,
    callQueues: `Colas de llamadas`,
    conferenceRooms: `Salas de conferencias`,
    virtualFax: `Fax virtual`,
    sharedVoicemail: `Buzón de voz compartido`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    title: `Filtro`,
    status: `Estado:`,
    assignedUser: `Usuario asignado:`,
    assignedProfile: `Perfil asignado:`,
    location: `Ubicación:`,
    active: `Activo`,
    inactive: `Inactivo`,
    all: `Todos`,
    clearFilters: `Limpiar filtros`,
    apply: `Aplicar`,
  },

  // ---------------------------------------------------------------------------
  // Placeholder pages
  // ---------------------------------------------------------------------------
  placeholder: {
    notImplemented: `La página de edición de {{typeLabel}} aún no está implementada.`,
    backToList: `Volver a {{listLabel}}`,
  },

  // ---------------------------------------------------------------------------
  // Create extension options
  // ---------------------------------------------------------------------------
  createOptions: {
    add: `Agregar`,
    addLine: `Agregar línea`,
    addRingGroup: `Agregar grupo de timbrado`,
    addCallQueue: `Agregar cola de llamadas`,
    addConferenceRoom: `Agregar sala de conferencias`,
    addVirtualFax: `Agregar máquina de fax virtual`,
    addSharedVoicemail: `Agregar buzón de voz compartido`,
  },

  // ---------------------------------------------------------------------------
  // Ordering options
  // ---------------------------------------------------------------------------
  ordering: {
    lastUpdatedNewest: `Última actualización (más reciente)`,
    lastUpdatedOldest: `Última actualización (más antigua)`,
    createdNewest: `Creado (más reciente)`,
    createdOldest: `Creado (más antiguo)`,
    nameAZ: `Nombre (A\u2013Z)`,
    nameZA: `Nombre (Z\u2013A)`,
    numberAsc: `Número (asc)`,
    numberDesc: `Número (desc)`,
  },

  // ---------------------------------------------------------------------------
  // Slug-to-label mappings (for breadcrumbs)
  // ---------------------------------------------------------------------------
  slugLabels: {
    line: `Línea`,
    ringGroup: `Grupo de timbrado`,
    callQueue: `Cola de llamadas`,
    conferenceRoom: `Sala de conferencias`,
    virtualFax: `Máquina de fax virtual`,
    sharedVoicemail: `Buzón de voz compartido`,
  },

  // ---------------------------------------------------------------------------
  // List labels for type slugs
  // ---------------------------------------------------------------------------
  listLabels: {
    lines: `Líneas`,
    ringGroups: `Grupos de timbrado`,
    callQueues: `Colas de llamadas`,
    conferenceRooms: `Salas de conferencias`,
    virtualFax: `Fax virtual`,
    sharedVoicemail: `Buzón de voz compartido`,
  },
} satisfies ExtensionsLocale
